<template>
  <div class="min-h-screen bg-black text-white">
    <Loader v-if="videoStore.loading || favoritesStore.loading" />

    <main v-else>
      <div v-if="!nonEmptyCategories?.length" class="flex items-center justify-center h-screen">
        <p class="text-xl text-white text-center">No videos found</p>
      </div>
      <section v-else v-for="(category, index) in nonEmptyCategories" :key="category.name" class="py-8">
        <div class="container mx-auto px-4">

          <h2 class="text-2xl font-bold mb-4 text-orange-500">{{ category.name }}</h2>

          <!-- Container that handles arrows + scroll -->
          <div class="relative">
            <button v-if="showLeftArrow(index)" @click="scrollLeft(index)"
              class="absolute z-10 left-0 top-0 bottom-0 flex items-center justify-center w-12 bg-gradient-to-r from-black/50 to-transparent text-white text-3xl hover:from-black/70 hover:to-black/20 rounded-r-lg transition">
              <font-awesome-icon icon="chevron-left" />
            </button>

            <!-- Horizontal scroll container -->
            <div class="flex space-x-4 overflow-x-auto scrollbar-hide" :ref="(el) => {
              if (el && !scrollContainers[index]) {
                scrollContainers[index] = el
              }
            }" :data-index="index" @scroll="onScroll(index)">
              <template v-if="category.name === 'Partners'">
                <div v-for="partner in category.partners" :key="partner.name" @click="loadPartnerFeed(partner.id)"
                  class="relative group p-5 flex-shrink-0 w-full xs:w-72 overflow-hidden rounded-lg cursor-pointer border-solid border-2 border-gray-800">
                  <img :src="partner.thumbnail" :alt="partner.title"
                    class="w-full transition-transform duration-300 group-hover:scale-110" />
                </div>
              </template>

              <template v-if="category.type === 'singlevideo'">
                <div v-for="video in category.videos" :key="video.id" @click="goToVideoDetail(video, category)"
                  class="relative group flex-shrink-0 w-48 overflow-hidden rounded-lg cursor-pointer">
                  <img :src="video.thumbnail2x3 || video.thumbnail" :alt="video.title"
                    class="w-full h-64 transition-transform duration-300 group-hover:scale-110" />
                  <div
                    class="absolute inset-0 bg-black bg-opacity-50 flex items-end p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div>
                      <h3 class="text-lg font-semibold mb-2">{{ video.title }}</h3>
                      <p class="text-sm">{{ formatDuration(video.content?.duration) }}</p>
                    </div>
                  </div>
                </div>
              </template>

              <template v-if="category.type === 'playlist'">
                <div v-for="series in category.series" :key="series.key" @click="goToSeriesDetail(series)"
                  class="relative group flex-shrink-0 w-48 overflow-hidden rounded-lg cursor-pointer">
                  <img :src="series.thumbnail2x3 || series.thumbnail" :alt="series.title"
                    class="w-full h-64 transition-transform duration-300 group-hover:scale-110" />
                  <div
                    class="absolute inset-0 bg-black bg-opacity-50 flex items-end p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div>
                      <h3 class="text-lg font-semibold mb-2">{{ series.title }}</h3>
                      <p class="text-sm">{{ series.description }}</p>
                    </div>
                  </div>
                </div>
              </template>
            </div>

            <button v-if="showRightArrow(index)" @click="scrollRight(index)"
              class="absolute z-10 right-0 top-0 bottom-0 flex items-center justify-center w-12 bg-gradient-to-l from-black/50 to-transparent text-white text-3xl hover:from-black/70 hover:to-black/20 rounded-r-lg transition">
              <font-awesome-icon icon="chevron-right" />
            </button>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<script setup>
import { computed, onMounted, watch } from 'vue'
import { useVideoStore } from '@/store/videoStore'
import router from '@/router'
import { useRoute } from 'vue-router'
import Loader from '@/components/Loader.vue';
import { formatDuration } from '@/utils/formatDuration'
import { useHorizontalScroll } from '@/composables/useHorizontalScroll'
import { generateSlug } from '@/utils/slugUtils'
import { analyticsEvent } from '@/utils/analytics';
import { useFavoritesStore } from '@/store/favoritesStore'
import { useConfigStore } from '@/store/configStore';
import { useAuth } from '@/composables/useAuth'
import { FEATURES } from '@/constants/features'

const defaultFeedId = import.meta.env.VITE_DEFAULT_FEED_ID

const videoStore = useVideoStore()
const route = useRoute()
const categories = computed(() => videoStore.videos)
const favoritesStore = useFavoritesStore()
const auth = useAuth()
const configStore = useConfigStore()


const favOnly = computed(() => configStore.features.includes(FEATURES.REGISTRATION_FAVORITES) && route.query.favs === '1')
const hasPartnerId = computed(() => {
  const originPartnerId = Array.isArray(route.query.originPartnerId)
    ? route.query.originPartnerId[0]
    : route.query.originPartnerId

  if (originPartnerId == null) return false
  return String(originPartnerId).trim().length > 0
})
const favoriteIdSet = computed(() => new Set(favoritesStore.favorites.map(f => f.id)))

const nonEmptyCategories = computed(() => {
  const src = categories.value || []

  const filterByFavorites = (cat) => {
    if (!favOnly.value) return cat

    // Hide Partners when filtering
    if (cat.name === 'Partners') return null

    if (cat.type === 'singlevideo' && Array.isArray(cat.videos)) {
      const vids = cat.videos.filter(v => favoriteIdSet.value.has(v.id))
      return vids.length ? { ...cat, videos: vids } : null
    }

    // Optional: If you want playlists to appear only when a series contains a favorited video,
    // you need a mapping from video -> series. If you have series.videos, you could:
    // if (cat.type === 'playlist' && Array.isArray(cat.series)) {
    //   const series = cat.series.filter(s => (s.videos || []).some(v => favoriteIdSet.value.has(v.id)))
    //   return series.length ? { ...cat, series } : null
    // }

    // Default: hide playlists in favorites mode unless you implement mapping
    if (cat.type === 'playlist') return null

    return null
  }

  const filtered = src
    .map(c => filterByFavorites(c) ?? null)
    .filter(Boolean)

  // If not filtering, keep original non-empty logic:
  if (!favOnly.value) {
    const conditions = {
      Partners: (category) => !hasPartnerId.value && category.partners?.length > 0,
      singlevideo: (category) => category.videos?.length > 0,
      playlist: (category) => category.series?.length > 0,
    }
    return src.filter((category) => {
      const condition = conditions[category.name] || conditions[category.type]
      return condition ? condition(category) : true
    })
  }

  // In favorites mode, we already filtered empties
  return filtered
})

const {
  scrollContainers,
  scrollLeft,
  scrollRight,
  onScroll,
  checkAllArrowsVisibility,
  showLeftArrow,
  showRightArrow
} = useHorizontalScroll()


async function loadData(feedId) {
  await videoStore.loadVideos(feedId)
  checkAllArrowsVisibility(nonEmptyCategories.value)
}

function loadPartnerFeed(partnerId) {
  const currentPartnerId = Array.isArray(route.params.partnerId)
    ? route.params.partnerId[0]
    : route.params.partnerId

  const existingPartnerId = Array.isArray(route.query.originPartnerId)
    ? route.query.originPartnerId[0]
    : route.query.originPartnerId

  const query = { ...route.query }

  if (!existingPartnerId && currentPartnerId) {
    query.originPartnerId = currentPartnerId
  } else if (existingPartnerId) {
    query.originPartnerId = existingPartnerId
  }

  router.push({ name: 'home', params: { partnerId }, query })
}

function goToVideoDetail(video, category) {
  analyticsEvent(video.title, "video_selection", "video_clicked");

  videoStore.selectVideo(video)
  videoStore.selectSection(category)
  const partnerId = route.params.partnerId
  const slug = generateSlug(video.title, video.id)
  router.push({ name: 'video-detail', params: { partnerId, slug }, query: { ...route.query } })
}

function goToSeriesDetail(series) {
  const partnerId = route.params.partnerId
  const slug = generateSlug(series.title, series.id)
  videoStore.selectSeries(series.id)
  router.push({ name: 'series-detail', params: { partnerId, slug }, query: { ...route.query } })
}

watch(
  () => route.params.partnerId,
  (newPartnerId) => {
    const feedId = newPartnerId || defaultFeedId
    loadData(feedId)
  }
)

async function loadFavorites() {
  if (!auth.isAuthenticated.value || !favOnly.value) return

  try {
    const feedId = route.params.partnerId || defaultFeedId
    await favoritesStore.fetchServerFavorites(feedId, auth.user.value?.app_metadata.mongo_id)
  } catch (error) {
    console.error('Error fetching server favorites:', error)
  }
}

watch(favOnly, loadFavorites)

onMounted(() => {
  const partnerId = route.params.partnerId
  const feedId = partnerId || defaultFeedId
  videoStore.selectVideo(null)
  loadData(feedId)
  loadFavorites()
})


</script>

<style scoped></style>
